import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, computed, ElementRef, inject, InjectionToken, input, model, signal } from '@angular/core';

let nextId = 0;

// Injection token for the accordion panel component
export const ACCORDION_PANEL = new InjectionToken<AccordionPanelComponent>('ACCORDION_PANEL');

@Component({
  selector: 'sb-accordion-panel',
  standalone: true,
  templateUrl: './accordion-panel.component.html',
  styleUrls: ['./accordion-panel.component.scss'],
  animations: [
    trigger('contentExpand', [
      state('collapsed', style({ height: '0', opacity: 0.4 })),
      state('expanded', style({ height: '*', opacity: 1 })),
      transition('collapsed => expanded', [animate('300ms ease-in-out')]),
      transition('expanded => collapsed', [animate('250ms ease-in-out')]),
    ]),
  ],
  host: {
    '[id]': 'id()',
    '[class.sb-expanded]': 'expanded()',
  },
  exportAs: 'accordionPanel',
  providers: [{ provide: ACCORDION_PANEL, useExisting: AccordionPanelComponent }],
})
export class AccordionPanelComponent {
  private readonly elementRef = inject(ElementRef<HTMLElement>);

  /**
   * Track if animation is in progress
   */
  isAnimating = signal(false);

  /**
   * Unique identifier for the accordion panel
   */
  readonly id = input<string>(`sb-accordion-panel-${nextId++}`);

  /**
   * Whether the accordion panel is disabled
   */
  readonly disabled = input<boolean>(false);

  /**
   * Signal for expanded state with two-way binding
   */
  readonly expanded = model<boolean>(false);

  /**
   * Animation state for the panel
   */
  readonly animationState = computed<'collapsed' | 'expanded'>(() => (this.expanded() ? 'expanded' : 'collapsed'));

  /**
   * Computed signal for header ID
   */
  readonly headerId = computed(() => `${this.id()}-header`);

  /**
   * Computed signal for content ID
   */
  readonly contentId = computed(() => `${this.id()}-content`);

  /**
   * Called when the animation starts
   */
  animationStarted(): void {
    this.isAnimating.set(true);
  }

  /**
   * Called when the animation completes
   */
  animationDone(): void {
    this.isAnimating.set(false);
  }

  /**
   * Toggle the expanded state
   */
  toggle(): void {
    if (this.disabled()) return;

    // Update the state
    const newValue = !this.expanded();
    this.expanded.set(newValue);
  }

  /**
   * Set expanded state
   */
  setExpanded(isExpanded: boolean): void {
    if (this.expanded() === isExpanded) return;

    this.expanded.set(isExpanded);
  }

  /**
   * Focus the header element of this panel
   */
  focus(): void {
    const headerElement = this.elementRef.nativeElement.querySelector('[role="button"]');
    if (headerElement instanceof HTMLElement) {
      headerElement.focus();
    }
  }
}
